import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  CheckoutEventSuccess,
  CheckoutInitialisation,
  CheckoutSuccessfulContract,
  CustomerDetails,
  PaymentFrequency,
  QuoteResponse,
  RegisteredItem,
  RegisteredItemProductDetails,
  RegistrationApplianceDetails,
  RegistrationRequest,
  RegistrationSystemCode,
  RegistriaUsersResponse,
  SerializationCodeResponse,
} from '@us-registration/util-models';
import { Observable } from 'rxjs';
import * as RegistrationActions from './registration.actions';
import * as RegistrationSelectors from './registration.selectors';

@Injectable({ providedIn: 'root' })
export class RegistrationFacade {
  constructor(private readonly store: Store) {}

  registrationLoading$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectRegistrationLoading
  );

  registeredItem$: Observable<RegisteredItem | undefined> = this.store.select(
    RegistrationSelectors.selectRegisteredItem
  );

  hasRegisteredItem$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasRegisteredItem
  );

  hasRegisteredItemProducts$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasRegisteredItemProducts
  );

  hasRegisteredItemOutOfGuarantee$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectRegisteredItemOutOfGuarantee
  );

  registeredItemProductDetails$: Observable<
    RegisteredItemProductDetails | undefined
  > = this.store.select(
    RegistrationSelectors.selectRegisteredItemProductDetails
  );

  registeredItemAnnualProductDetails$: Observable<
    RegisteredItemProductDetails | undefined
  > = this.store.select(
    RegistrationSelectors.selectRegisteredItemAnnualProductDetails
  );

  registeredItemProducts$: Observable<
    RegisteredItemProductDetails[] | undefined
  > = this.store.select(RegistrationSelectors.selectRegisteredItemProducts);

  registeredItemMerchandiseBrand$: Observable<string | undefined> =
    this.store.select(
      RegistrationSelectors.selectRegisteredItemMerchandiseBrand
    );

  registeredItemQuoteNumber$: Observable<string | undefined> =
    this.store.select(RegistrationSelectors.selectRegisteredItemQuoteNumber);

  hasRegisteredItemQuoteNumber$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasRegisteredItemQuoteNumber
  );

  hasRegisteredItemStandardPremium$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasRegisteredItemStandardPremium
  );

  registrationCustomerDetails$: Observable<CustomerDetails | undefined> =
    this.store.select(RegistrationSelectors.selectRegistrationCustomerDetails);

  registeredItemHasAnnualPayment$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectRegisteredItemHasAnnualPayment
  );

  registeredItemAppliances$: Observable<
    RegistrationApplianceDetails[] | undefined
  > = this.store.select(RegistrationSelectors.selectRegisteredItemAppliances);

  serializationCodeResponse$: Observable<
    SerializationCodeResponse | undefined
  > = this.store.select(RegistrationSelectors.selectSerializationCodeResponse);

  registrationSystemCode$: Observable<RegistrationSystemCode> =
    this.store.select(RegistrationSelectors.selectRegistrationSystemCode);

  registrationRequest$: Observable<RegistrationRequest | undefined> =
    this.store.select(RegistrationSelectors.selectRegistrationRequest);

  registriaUserResponse$: Observable<RegistriaUsersResponse | undefined> =
    this.store.select(RegistrationSelectors.selectRegistriaUserResponse);

  userToken$: Observable<string | undefined> = this.store.select(
    RegistrationSelectors.selectUserToken
  );

  //WIB Necessary State Selectors Start

  quoteResponse$: Observable<QuoteResponse | undefined> = this.store.select(
    RegistrationSelectors.selectQuote
  );

  hasQuoteResponse$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasQuoteResponse
  );

  isQuoteResponseNoContent$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectIsQuoteResponseNoContent
  );

  isQuoteResponseSuccess$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectIsQuoteResponseSuccess
  );

  paymentFrequency$: Observable<string | undefined> = this.store.select(
    RegistrationSelectors.selectPaymentFrequency
  );

  quoteNetTotalAmount$: Observable<number | undefined> = this.store.select(
    RegistrationSelectors.selectQuoteNetTotalAmount
  );

  checkoutInitialisation$: Observable<CheckoutInitialisation | undefined> =
    this.store.select(RegistrationSelectors.selectCheckoutInitialisation);

  hasInitialisedCheckout$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasInitialisedCheckout
  );

  customerFirstName$: Observable<string | undefined> = this.store.select(
    RegistrationSelectors.selectCustomerFirstName
  );

  checkoutSuccessfulContracts$: Observable<
    CheckoutSuccessfulContract | undefined
  > = this.store.select(RegistrationSelectors.selectCheckoutSuccessfulContract);

  contractId$: Observable<string | undefined> = this.store.select(
    RegistrationSelectors.selectCheckoutSuccessContractId
  );

  effectiveDate$: Observable<string | undefined> = this.store.select(
    RegistrationSelectors.selectQuoteEffectiveDate
  );

  quoteHasAnnualPayment$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectQuoteHasAnnualPayment
  );

  protectionDetailsPaymentOptions$ = this.store.select(
    RegistrationSelectors.selectProtectionDetailsPaymentOptions
  );

  protectionDetailsPrepayPrice$ = this.store.select(
    RegistrationSelectors.selectPrepayPrice
  );

  protectionDetailsSubscriptionPrice$ = this.store.select(
    RegistrationSelectors.selectSubscriptionPrice
  );

  hasDiscountPrice$: Observable<boolean> = this.store.select(
    RegistrationSelectors.selectHasDiscountPrice
  );

  subscriptionDiscountPrice$: Observable<number | undefined> =
    this.store.select(RegistrationSelectors.selectSubscriptionDiscountPrice);

  //WIB Necessary State Selectors End

  registerAppliance(registrationRequest: RegistrationRequest): void {
    this.store.dispatch(
      RegistrationActions.registerApplianceRequest({
        registrationRequest,
      })
    );
  }

  getQuote(registrationRequest: RegistrationRequest): void {
    this.store.dispatch(
      RegistrationActions.getQuote({
        registrationRequest,
      })
    );
  }

  addSerializationCodeResponse(
    serializationCodeResponse: SerializationCodeResponse
  ): void {
    this.store.dispatch(
      RegistrationActions.addSerializationCodeResponse({
        serializationCodeResponse,
      })
    );
  }

  addRegistrationSystemCode(
    registrationSystemCode: RegistrationSystemCode
  ): void {
    this.store.dispatch(
      RegistrationActions.addRegistrationSystemCode({
        registrationSystemCode,
      })
    );
  }

  addRegistriaUserResponse(
    registriaUserResponse: RegistriaUsersResponse
  ): void {
    this.store.dispatch(
      RegistrationActions.addRegistriaUserResponse({
        registriaUserResponse,
      })
    );
  }

  addUserToken(userToken: string): void {
    this.store.dispatch(
      RegistrationActions.addUserToken({
        userToken,
      })
    );
  }

  addCheckoutInitialisation(
    checkoutInitialisation: CheckoutInitialisation
  ): void {
    this.store.dispatch(
      RegistrationActions.addCheckoutInitialisation({
        checkoutInitialisation,
      })
    );
  }

  addCheckoutSuccess(checkoutSuccess: CheckoutEventSuccess): void {
    this.store.dispatch(
      RegistrationActions.checkoutSuccess({
        checkoutSuccess,
      })
    );
  }

  addPaymentFrequency(paymentFrequency: PaymentFrequency): void {
    this.store.dispatch(
      RegistrationActions.addPaymentFrequency({
        paymentFrequency,
      })
    );
  }
}
